import React from 'react';
import { useEffect, useState } from 'react';
import SiteChrome from './SiteChrome';
import api from '../services/api';

const privacyUrl = 'https://bettermedaily.co.za/privacy.html';
const mainTermsUrl = 'https://bettermedaily.co.za/terms.html';
const supportEmail = 'affiliates@bettermedaily.co.za';

const Terms = () => {
  const [minimumPayoutAmount, setMinimumPayoutAmount] = useState(100);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const pricing = await api.getPricing();
        setMinimumPayoutAmount(Number(pricing?.affiliateMinimumPayoutAmount || 100));
      } catch (error) {
        console.warn('Failed to load affiliate pricing settings:', error);
      }
    };

    loadPricing();
  }, []);

  return (
    <SiteChrome>
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Affiliate Program Terms & Conditions</h1>
          
          <div className="prose prose-gray max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Program Overview</h2>
            <p className="text-gray-600 mb-6">
              The Better Me Affiliate Program allows you to earn commissions by referring new users to the Better Me mobile application. As an affiliate, you'll receive compensation for qualified sign-ups and premium subscription conversions.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Eligibility Requirements</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
              <li>Must be at least 18 years of age</li>
              <li>Must have a valid South African bank account for payouts</li>
              <li>Must comply with all applicable laws and regulations</li>
              <li>Cannot have been previously terminated from the program</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Commission Structure</h2>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-2">Commission Rates:</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>R1.00 per qualified active user sign-up</li>
                <li>50% commission on premium subscription revenue from referred users</li>
                <li>Minimum payout threshold: R{minimumPayoutAmount.toFixed(2)}</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Allowed Marketing Methods</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
              <li>Social media sharing (Facebook, Twitter, Instagram, etc.)</li>
              <li>Personal blogs and websites</li>
              <li>Email marketing to your own subscriber list</li>
              <li>YouTube videos and content creation</li>
              <li>Word-of-mouth referrals</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Prohibited Activities</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
              <li>Spam or unsolicited promotional messages</li>
              <li>Misleading or false advertising claims</li>
              <li>Claims that Better Me provides therapy, diagnosis, treatment, emergency intervention, or medical/psychological advice</li>
              <li>Claims that Better Me provides legal, investment, credit, tax, or insurance advice</li>
              <li>Purchasing paid search ads using Better Me trademarks</li>
              <li>Creating fake accounts or fraudulent sign-ups</li>
              <li>Violating app store policies or terms of service</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Payment Terms</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
              <li>Payouts processed monthly on the last day of the month</li>
              <li>Minimum payout amount: R{minimumPayoutAmount.toFixed(2)}</li>
              <li>Payments made via bank transfer to South African accounts only</li>
              <li>Commissions are confirmed after a 30-day verification period</li>
              <li>Refunds, chargebacks, invalid referrals, suspicious traffic, or incorrect bank details may delay, reduce, or reverse payout approval</li>
              <li>Unpaid commissions below minimum threshold roll over to next period</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Tracking & Attribution</h2>
            <p className="text-gray-600 mb-6">
              Referrals are tracked using unique affiliate links and cookies. Cookies have a 30-day duration. A referral is considered qualified when the user signs up and remains active for at least 7 days.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Public Marketing and Crisis Handling</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
              <li>Promote Better Me as a journaling, habit tracking, decision-reflection, and spending-awareness app only</li>
              <li>Do not guarantee user outcomes, health improvements, financial results, or affiliate earnings</li>
              <li>If someone mentions self-harm or severe distress, do not counsel or diagnose them as an affiliate; tell them to contact local emergency services, 112/10111 in South Africa, or Lifeline South Africa on 0861 322 322</li>
              <li>Use consent-based marketing only and remove recipients from future messages if they opt out</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Termination</h2>
            <p className="text-gray-600 mb-6">
              Better Me reserves the right to terminate any affiliate account for violation of these terms, fraudulent activity, or any reason at our sole discretion. Upon termination, all unpaid commissions will be forfeited.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-600 mb-6">
              Better Me shall not be liable for any indirect, incidental, or consequential damages arising from your participation in the affiliate program.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Privacy, Main App Terms, and Contact</h2>
            <p className="text-gray-600 mb-6">
              Better Me's public <a href={privacyUrl} className="text-primary-600 hover:underline">Privacy Policy</a> and <a href={mainTermsUrl} className="text-primary-600 hover:underline">Main App Terms</a> also apply where relevant to user data and product descriptions. For affiliate support, payout questions, or policy clarification, email <a href={`mailto:${supportEmail}`} className="text-primary-600 hover:underline">{supportEmail}</a>. Support hours: Monday-Friday, 09:00-17:00 SAST. Operating country: South Africa.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-8">
              <p className="text-sm text-yellow-800">
                <strong>Last Updated:</strong> April 3, 2026
                <br />
                These terms are subject to change at Better Me's discretion. Continued participation in the program constitutes acceptance of any modifications.
              </p>
            </div>
          </div>

                  </div>
      </div>
    </SiteChrome>
  );
};

export default Terms;
